import { RoleStatus } from '@b-stock/account-api-client'
import {
  useAccessToken,
  useAccessTokenRoles,
  useDeliveryMethodModal,
} from '@b-stock/fe-core/bstock-next/components'
import { canReadAsBuyer, useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedOfferingListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { interpUri } from '@b-stock/fe-core/bstock-react'
import { ListingDtoListingTypeEnum } from '@b-stock/listing-api-client'
import type { OrderResponseDto } from '@b-stock/order-api-client'

import buyUrlPrefix from '@/buyer-portal/buyUrlPrefix'
import useAutoQuoteGeneration, {
  type AutoQuoteGenerationError,
  type AutoQuoteGenerationState,
} from '@/buyer-portal/components/features/listing/hooks/useAutoQuoteGeneration'
import { useShippingModalTracking } from '@/buyer-portal/components/features/listing/hooks/useShippingModalTracking'
import canChangeShipping from '@/buyer-portal/components/features/order/OrderDetailsLayout/canChangeShipping'
import ShippingSummary from '@/buyer-portal/components/ShippingSummary'
import { getEditDeliveryMethodRedirectUri } from '@/buyer-portal/helpers/shipping'
import useCompatibleRouter from '@/buyer-portal/helpers/useCompatibleRouter'
import { FeatureFlags } from '@/helpers/featureFlags'

import useChangeCarrier from './ShippingInformationCard/useChangeCarrier'

type ListingShippingSummaryBaseProps = {
  auctionOrOfferingEnded?: boolean
  buyerWon?: boolean
  order?: OrderResponseDto | null
  selectedQuote?: OrchestratedQuote | null
  trackButtonClick: (buttonName: string) => void
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
  renderResponsiveView?: boolean
}

type ListingShippingSummaryProps = ListingShippingSummaryBaseProps & {
  auctionAutoQuoteGeneration?: AutoQuoteGenerationState
}

type ListingShippingSummaryCoreProps = ListingShippingSummaryBaseProps & {
  autoQuoteGenerationError: AutoQuoteGenerationError | undefined
  autoQuoteGenerationLoading: boolean
}

const ListingShippingSummaryCore = ({
  auctionOrOfferingEnded,
  buyerWon,
  order,
  selectedQuote,
  trackButtonClick,
  listing,
  renderResponsiveView,
  autoQuoteGenerationError,
  autoQuoteGenerationLoading,
}: ListingShippingSummaryCoreProps) => {
  const router = useCompatibleRouter()
  const accessToken = useAccessToken()
  const roles = useAccessTokenRoles()
  const isIntendedBuyer = roles?.buyer === RoleStatus.Intended

  const p2sMetricsVariant = useFlag(FeatureFlags.ENABLE_LDP_P2S_METRICS_AB)
  const isLoggedIn = canReadAsBuyer(accessToken)
  // TODO: Move Costco exclusion to flag config when flag becomes object type
  const isCostco = listing.seller.storefront?.slug === 'costco'
  const p2sVariant =
    p2sMetricsVariant !== 'off' && isLoggedIn && !isCostco
      ? p2sMetricsVariant
      : undefined

  const isContract =
    listing.listingType === ListingDtoListingTypeEnum.Contract &&
    !!listing.contract

  const canEditDeliveryMethod =
    !auctionOrOfferingEnded || (order && canChangeShipping(order))

  const { editDeliveryMethod, deliveryMethodChangePending } =
    useDeliveryMethodModal()

  const shippingSummaryLoading =
    deliveryMethodChangePending || autoQuoteGenerationLoading

  const isOrderBeingCreated = !isContract && !order && buyerWon

  const trackingCallbacks = useShippingModalTracking(listing, selectedQuote)

  const {
    carrierModal,
    hasMultipleCarrierOptions,
    isCarrierChanging,
    openCarrierModal,
    shouldShowCarrierChange,
  } = useChangeCarrier({
    hasAutoQuoteError: !!autoQuoteGenerationError,
    listing,
    order,
    selectedQuote,
  })

  const handleEditDeliveryMethod = canEditDeliveryMethod
    ? () => {
        if (order) {
          if (!order.buyerAccountId) {
            throw new Error(
              `order.buyerAccountId was undefined for ${order._id} - this is unexpected`
            )
          }
          const destination = `${buyUrlPrefix}${interpUri`/user/${order.buyerAccountId}/orders/details/${order._id}?editShipping`}`
          router.push(destination)
        } else {
          editDeliveryMethod({
            ...trackingCallbacks,
            redirectURI: getEditDeliveryMethodRedirectUri(),
          })

          trackButtonClick(selectedQuote ? 'edit' : 'add_address')
        }
      }
    : undefined

  return (
    <>
      {(canReadAsBuyer(accessToken) || isIntendedBuyer) &&
      (!auctionOrOfferingEnded || buyerWon) ? (
        <ShippingSummary
          renderResponsiveView={renderResponsiveView}
          onEdit={handleEditDeliveryMethod}
          onEditLoading={isOrderBeingCreated}
          listingData={listing}
          selectedQuote={selectedQuote}
          loading={shippingSummaryLoading}
          autoQuoteGenerationError={autoQuoteGenerationError}
          isIntendedBuyer={isIntendedBuyer}
          groups={listing.lot.groups}
          onCarrierChange={
            shouldShowCarrierChange ? openCarrierModal : undefined
          }
          isCarrierChangeDisabled={
            !hasMultipleCarrierOptions || isCarrierChanging
          }
          onCarrierChangeLoading={
            shouldShowCarrierChange ? isCarrierChanging : undefined
          }
          hasMultipleCarrierOptions={hasMultipleCarrierOptions}
          p2sVariant={p2sVariant}
        />
      ) : null}
      {carrierModal}
    </>
  )
}

const ListingShippingSummaryWithProvidedAutoQuote = ({
  auctionAutoQuoteGeneration,
  ...baseProps
}: ListingShippingSummaryBaseProps & {
  auctionAutoQuoteGeneration: AutoQuoteGenerationState
}) => (
  <ListingShippingSummaryCore
    {...baseProps}
    autoQuoteGenerationError={auctionAutoQuoteGeneration.error}
    autoQuoteGenerationLoading={auctionAutoQuoteGeneration.isLoading}
  />
)

const ListingShippingSummaryWithOwnedAutoQuote = (
  baseProps: ListingShippingSummaryBaseProps
) => {
  const { error, isLoading } = useAutoQuoteGeneration(baseProps.listing)

  return (
    <ListingShippingSummaryCore
      {...baseProps}
      autoQuoteGenerationError={error}
      autoQuoteGenerationLoading={isLoading}
    />
  )
}

export const ListingShippingSummary = ({
  auctionAutoQuoteGeneration,
  ...baseProps
}: ListingShippingSummaryProps) =>
  auctionAutoQuoteGeneration !== undefined ? (
    <ListingShippingSummaryWithProvidedAutoQuote
      {...baseProps}
      auctionAutoQuoteGeneration={auctionAutoQuoteGeneration}
    />
  ) : (
    <ListingShippingSummaryWithOwnedAutoQuote {...baseProps} />
  )
